import React, { useEffect } from 'react'

const TimelineDetail = ({ timelineItem, onClose }) => {
  if (!timelineItem) return null

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        console.log('Escape key pressed') // Debug log
        if (typeof onClose === 'function') {
          onClose()
        }
      }
    }
    
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  // Handle backdrop click
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      console.log('Backdrop clicked') // Debug log
      if (typeof onClose === 'function') {
        onClose()
      }
    }
  }

  // Handle close button click
  const handleCloseClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    console.log('Close button clicked') // Debug log
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="timeline-modal-title"
      aria-describedby="timeline-modal-description"
    >
      <div className="glass-card rounded-3xl p-8 max-w-4xl max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
        {/* Background Gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${timelineItem.color} opacity-5 rounded-3xl pointer-events-none`}></div>
        
        {/* Close Button */}
        <button
          onClick={handleCloseClick}
          className="absolute top-4 right-4 p-3 rounded-full bg-red-500/20 hover:bg-red-500/40 transition-all duration-300 text-white hover:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-transparent z-[60] backdrop-blur-sm border border-red-500/30"
          aria-label="Close timeline details modal"
          type="button"
          style={{ position: 'absolute', top: '1rem', right: '1rem' }}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-center space-x-4 mb-6">
            <div className={`w-4 h-4 bg-gradient-to-r ${timelineItem.color} rounded-full animate-pulse`}></div>
            <span className="text-white/80 font-mono text-lg">{timelineItem.year}</span>
          </div>

          <h2 id="timeline-modal-title" className="text-3xl md:text-4xl font-bold text-white mb-3">
            {timelineItem.title}
          </h2>
          <p id="timeline-modal-description" className="text-xl text-white/80 mb-6">{timelineItem.subtitle}</p>

          {/* Description */}
          <div className="glass-card rounded-2xl p-6 mb-8">
            <p className="text-white/90 leading-relaxed">
              {timelineItem.description}
            </p>
          </div>

          {/* Key Events */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Key Events</span>
            </h3>
            <div className="space-y-4">
              {timelineItem.keyEvents.map((event, index) => (
                <div key={index} className="glass-card rounded-xl p-4 hover:scale-105 transition-transform duration-300">
                  <div className="flex items-start space-x-4">
                    <div className={`w-8 h-8 bg-gradient-to-r ${timelineItem.color} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-white/80 font-mono text-sm bg-white/10 px-2 py-1 rounded">
                          {event.year}
                        </span>
                        <h4 className="text-white font-semibold">{event.event}</h4>
                      </div>
                      <p className="text-white/80 text-sm">{event.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>Key Technologies</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {timelineItem.technologies.map((tech, index) => (
                <div
                  key={index}
                  className={`glass-card rounded-lg p-3 text-center hover:scale-105 transition-all duration-300 border border-white/20`}
                >
                  <div className={`w-2 h-2 bg-gradient-to-r ${timelineItem.color} rounded-full mx-auto mb-2`}></div>
                  <span className="text-white/90 text-sm font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimelineDetail
